import { lazy, useEffect, type ComponentType } from 'react';
import { matchPath, Navigate, Route, Routes } from 'react-router-dom';
import { FigmaEmbedActivityReporter } from '@/components/FigmaEmbedActivityReporter';
import { FigmaEmbedNavigationGuard } from '@/components/FigmaEmbedNavigationGuard';
import { RecipeRedirect } from '@/components/Recipe/RecipeRedirect.tsx';
import { TOOLKIT_PATH } from '@/components/Dashboard/enums/shared';
import { ProtectedRoutes } from './ProtectedRoutes';
import { loadInBackground } from './providers/chunkReload';
import { PricingPageContainer } from './enums/pricing-page.enum';
import { ToolPreviewPage } from './pages/ToolPreviewPage';
import { PricingTablePage } from './pages/PricingTablePage';
import { TeamInviteActivationPage } from './pages/TeamInviteActivationPage';
import { Dashboard } from './pages/Dashboard';
import { SignInPage } from './pages/SignInPage';
import { GeneratePage } from './pages/GeneratePage';
import { WorkshopPage } from './pages/WorkshopPage';
import { IdpInitiatedSignInPage } from './pages/IdpInitiatedSignInPage';
import FigmaOAuthCallback from './pages/FigmaOAuthCallback';
import type { User } from './types/auth.types';

const EDITOR_PREFETCH_DELAY_MS = 2000;

/**
 * Splits a route's page into its own chunk. When the app opens on `path`, the chunk starts downloading right away:
 * `AuthProvider` holds back the router until the user is loaded, so waiting for the route to render would only start
 * the download after sign-in.
 */
function lazyRoute<P extends object>(path: string, load: () => Promise<ComponentType<P>>) {
  if (matchPath(path, window.location.pathname)) void load().catch(() => undefined);
  return lazy(async () => ({ default: await load() }));
}

const loadRecipe = () => import('./pages/Recipe').then((m) => m.Recipe);

const Recipe = lazyRoute('/flow/:recipeId', loadRecipe);
const ToolView = lazyRoute('/tool/:toolId', () => import('./pages/ToolView').then((m) => m.ToolView));
const Settings = lazyRoute('/settings', () => import('./pages/Settings').then((m) => m.Settings));
const EmbedWorkflow = lazyRoute('/embed/flow/:recipeId', () =>
  import('./pages/EmbedWorkflow').then((m) => m.EmbedWorkflow),
);
const CommunityEmbedWorkflow = lazyRoute('/embed/community/flow/:recipeId', () =>
  import('./pages/CommunityEmbedWorkflow').then((m) => m.CommunityEmbedWorkflow),
);
const CommunityEmbedTool = lazyRoute('/embed/community/tool/:toolId', () =>
  import('./pages/CommunityEmbedTool').then((m) => m.CommunityEmbedTool),
);
const FileViewer = lazyRoute('/view/*', () => import('./pages/FileViewer').then((m) => m.FileViewer));

export const Router = ({ currentUser, isSigningIn }: { currentUser: User | null; isSigningIn: boolean }) => {
  // Most sessions open a workflow, so fetch the editor once the current page has settled rather than on that click.
  useEffect(() => {
    const timeout = setTimeout(() => loadInBackground(loadRecipe), EDITOR_PREFETCH_DELAY_MS);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <>
      <FigmaEmbedNavigationGuard />
      <FigmaEmbedActivityReporter />
      <Routes>
        {/* Protected Routes */}
        <Route element={<ProtectedRoutes user={currentUser} />}>
          <Route path="/settings" element={<Settings user={currentUser!} />} />
          <Route path="/pricing" element={<PricingTablePage container={PricingPageContainer.Page} />} />
        </Route>
        <Route path="/recipe/:recipeId" element={<RecipeRedirect />} />
        <Route path="/flow/:recipeId" element={<Recipe user={currentUser} />} />
        <Route path="/tool/preview/:workflowId" element={<ToolPreviewPage user={currentUser} />} />
        <Route path="/tool/:toolId" element={<ToolView user={currentUser} />} />
        {/* Route for Figma embed mode: /embed/ */}
        <Route path="/embed/flow/:recipeId" element={<EmbedWorkflow />} />
        <Route path="/embed/community/flow/:recipeId" element={<CommunityEmbedWorkflow />} />
        <Route path="/embed/community/tool/:toolId" element={<CommunityEmbedTool />} />
        <Route path="/activation" element={<TeamInviteActivationPage />} />
        <Route path="/view/*" element={<FileViewer />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/generate" element={<GeneratePage user={currentUser} />} />
        <Route path="/workshops" element={<WorkshopPage user={currentUser} isSigningIn={isSigningIn} />} />
        <Route path="/idp-signin" element={<IdpInitiatedSignInPage />} />
        <Route path="/share" element={<FigmaOAuthCallback />} />
        <Route
          path="/"
          element={currentUser && !isSigningIn ? <Dashboard user={currentUser} /> : <Navigate to="/signin" replace />}
        />
        <Route
          path="/folders/:folderId"
          element={currentUser && !isSigningIn ? <Dashboard user={currentUser} /> : <Navigate to="/signin" replace />}
        />
        <Route
          path={TOOLKIT_PATH}
          element={currentUser && !isSigningIn ? <Dashboard user={currentUser} /> : <Navigate to="/signin" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};
